import React, { useState, useEffect, useMemo, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { GALLERY } from "@/constants/testIds";
import { resolveMediaUrl } from "@/lib/api";

const CATEGORY_LABELS = {
  all: "All Work",
  detailing: "Auto Detailing",
  pressure_washing: "Pressure Washing",
  marine: "Marine",
  before_after: "Before / After",
};

const DEFAULT_INITIAL = 12;
const PAGE_STEP = 12;

export default function Gallery({ images = [] }) {
  const list = images.length
    ? images
    : [
        {
          id: "d1",
          url: "https://images.unsplash.com/photo-1608506375591-b90e1f955e4b?crop=entropy&cs=srgb&fm=jpg&q=85",
          title: "Ceramic Finish",
          category: "detailing",
        },
        {
          id: "d2",
          url: "https://images.unsplash.com/photo-1565689876697-e467b6c54da2?crop=entropy&cs=srgb&fm=jpg&q=85",
          title: "Full Foam Wash",
          category: "detailing",
        },
        {
          id: "d3",
          url: "https://images.unsplash.com/photo-1711098256664-d2ea62e6e376?crop=entropy&cs=srgb&fm=jpg&q=85",
          title: "Driveway Wash",
          category: "pressure_washing",
        },
      ];

  const categories = useMemo(() => {
    const set = new Set(list.map((i) => i.category || "detailing"));
    return ["all", ...Array.from(set)];
  }, [list]);

  const [activeCat, setActiveCat] = useState("all");
  const [visibleCount, setVisibleCount] = useState(DEFAULT_INITIAL);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const filtered = useMemo(() => {
    if (activeCat === "all") return list;
    return list.filter((i) => (i.category || "detailing") === activeCat);
  }, [list, activeCat]);

  useEffect(() => {
    setVisibleCount(DEFAULT_INITIAL);
  }, [activeCat]);

  const shown = filtered.slice(0, visibleCount);

  const openLightbox = useCallback((idx) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(-1), []);
  const nextImage = useCallback(
    () => setLightboxIndex((i) => (i + 1) % filtered.length),
    [filtered.length]
  );
  const prevImage = useCallback(
    () => setLightboxIndex((i) => (i - 1 + filtered.length) % filtered.length),
    [filtered.length]
  );

  useEffect(() => {
    if (lightboxIndex < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") nextImage();
      else if (e.key === "ArrowLeft") prevImage();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex, closeLightbox, nextImage, prevImage]);

  const active = lightboxIndex >= 0 ? filtered[lightboxIndex] : null;

  return (
    <section
      id="gallery"
      data-testid={GALLERY.root}
      className="relative py-28 md:py-36 bg-[#050505]"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="mb-10 md:mb-14 max-w-3xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-cyan-400" />
            <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
              Our Work
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-none text-white">
            Results that <span className="text-cyan-400">speak for themselves.</span>
          </h2>
          <p className="mt-6 text-zinc-400 leading-relaxed">
            {list.length}+ real jobs done for real customers. Click any image to
            open it full-screen.
          </p>
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filter">
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`gallery-filter-${c}`}
              onClick={() => setActiveCat(c)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                activeCat === c
                  ? "border-cyan-400 bg-cyan-400 text-black font-bold"
                  : "border-white/10 bg-white/[0.02] text-zinc-300 hover:border-cyan-400/60 hover:text-cyan-400"
              }`}
            >
              {CATEGORY_LABELS[c] || c.replace(/_/g, " ")}
              <span className="ml-2 opacity-60">
                {c === "all" ? list.length : list.filter((i) => (i.category || "detailing") === c).length}
              </span>
            </button>
          ))}
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {shown.map((img, i) => (
            <button
              type="button"
              key={img.id || i}
              data-testid={GALLERY.item(i)}
              onClick={() => openLightbox(i)}
              className={`group relative overflow-hidden border border-white/10 bg-zinc-900 block text-left cursor-zoom-in aspect-square ${
                i % 7 === 0 ? "md:col-span-2 md:row-span-2 md:aspect-auto" : ""
              }`}
              style={{
                backgroundImage:
                  "linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%)",
              }}
            >
              <img
                src={resolveMediaUrl(img.url)}
                alt={img.title || "Gallery"}
                loading="lazy"
                decoding="async"
                width="800"
                height="800"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                onLoad={(e) => e.currentTarget.classList.add("opacity-100")}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-70 group-hover:opacity-90 transition-opacity" />
              {/* Zoom hint on hover */}
              <div className="absolute top-3 right-3 w-8 h-8 bg-black/70 backdrop-blur-md border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4 text-cyan-400" />
              </div>
              {img.title && (
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="text-[9px] md:text-[10px] uppercase tracking-widest text-cyan-400 mb-1">
                    {(img.category || "Detail").replace(/_/g, " ")}
                  </div>
                  <div className="text-white font-heading font-bold uppercase tracking-tight text-xs md:text-sm lg:text-base line-clamp-2">
                    {img.title}
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Load more */}
        {visibleCount < filtered.length && (
          <div className="mt-12 flex justify-center">
            <button
              type="button"
              data-testid="gallery-load-more"
              onClick={() => setVisibleCount((v) => v + PAGE_STEP)}
              className="px-8 h-12 uppercase tracking-widest text-xs font-semibold border border-white/20 bg-white/[0.02] text-white hover:border-cyan-400 hover:bg-cyan-400 hover:text-black transition-colors"
            >
              Load more ({filtered.length - visibleCount} left)
            </button>
          </div>
        )}

        {shown.length === 0 && (
          <div className="text-center text-zinc-500 py-16 border border-white/10 bg-white/[0.02]">
            No images in this category yet.
          </div>
        )}
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          data-testid="gallery-lightbox"
          onClick={closeLightbox}
        >
          <button
            type="button"
            data-testid="gallery-lightbox-close"
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-4 right-4 md:top-6 md:right-6 z-10 w-11 h-11 border border-white/20 bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {filtered.length > 1 && (
            <>
              <button
                type="button"
                data-testid="gallery-lightbox-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 border border-white/20 bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors"
                aria-label="Previous"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                data-testid="gallery-lightbox-next"
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 border border-white/20 bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:border-cyan-400 hover:text-cyan-400 transition-colors"
                aria-label="Next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div
            className="relative max-w-6xl w-full max-h-[90vh] flex flex-col items-center justify-center px-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={active.id}
              src={resolveMediaUrl(active.url)}
              alt={active.title || "Gallery image"}
              data-testid="gallery-lightbox-image"
              className="max-w-full max-h-[80vh] w-auto h-auto object-contain shadow-[0_0_60px_rgba(0,229,255,0.15)]"
            />
            <div className="mt-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-cyan-400 mb-1">
                {(active.category || "Detail").replace(/_/g, " ")}
              </div>
              <div className="text-white font-heading font-bold uppercase tracking-tight text-base md:text-lg">
                {active.title}
              </div>
              <div className="mt-1 text-xs text-zinc-500 uppercase tracking-widest">
                {lightboxIndex + 1} / {filtered.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
